/* Begin Baby Care storefront interaction publisher.
 * The Google & YouTube app owns GA4 page views and ecommerce events. This file
 * publishes only additional interactions for a consent-aware Shopify pixel.
 */
(() => {
  'use strict';

  if (window.__bbcGa4EventsLoaded) return;
  window.__bbcGa4EventsLoaded = true;

  const BANNER_SELECTOR = '[data-carousel-slide], .bbc-wrap-rush-banner, .bbc-dash-promo, .home-platform-hero';
  const CTA_SELECTOR = [
    '[data-bbc-cta]',
    '.home-quick-navigation__link',
    '.home-card-row__card',
    '.home-platform-hero__button',
    '.home-platform-hero__age-card',
    '.home-platform-carousel__button',
    '.home-platform-carousel__age-card',
    '.bbc-games-hub__button',
    '.bbc-account-action',
    '.header__icon--account',
    'main a[class*="__button"]',
    'main button[class*="__button"]',
    'main a.button',
    'main button.button',
    'main a.btn',
    'main button.btn',
    'main a[role="button"]',
    '.baby-soother__collection',
    '.baby-soother__round-action',
    '.baby-soother__record-button',
    '.bbc-diaper-dash [data-action]',
    '.bbc-wrap-rush [data-action]',
    '#bbc-install-action',
  ].join(', ');
  const viewed = new Set();
  const viewTimers = new WeakMap();
  const GAME_EVENT_FIELDS = Object.freeze({
    game_start: ['run_type', 'entry_point'],
    game_menu_open: ['game_state'],
    game_pause: ['pause_source', 'elapsed_seconds', 'difficulty_tier'],
    game_resume: ['elapsed_seconds', 'difficulty_tier'],
    game_catch: ['item_type', 'difficulty_tier', 'score_awarded', 'elapsed_seconds'],
    game_miss: ['item_type', 'difficulty_tier', 'anger_added', 'anger_band', 'elapsed_seconds'],
    game_difficulty_increase: ['from_tier', 'to_tier', 'speed_multiplier', 'baby_speed_multiplier'],
    game_over: ['final_score', 'best_score', 'duration_seconds', 'catch_pee', 'catch_poop', 'miss_pee', 'miss_poop', 'spawn_pee', 'spawn_poop', 'difficulty_tier', 'anger_final', 'new_personal_best'],
    game_replay: ['previous_score', 'previous_best', 'duration_seconds', 'difficulty_tier'],
    game_partner_select: ['partner_id'],
    game_setting_change: ['setting', 'enabled'],
    game_exit: ['exit_state', 'final_score', 'duration_seconds', 'difficulty_tier'],
  });

  function slug(value) {
    return String(value || '').toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'unknown';
  }

  function path() {
    const value = window.location.pathname || '/';
    if (value.length > 150 || !/^\/[a-z0-9/_-]*$/i.test(value) || /\/(account|orders|checkouts)\//i.test(value)) return '/other';
    return value;
  }

  function destination(element) {
    const href = element.getAttribute('href');
    if (!href) return '';
    try {
      const target = new URL(href, window.location.href);
      return target.origin === window.location.origin
        ? target.pathname.length <= 150 && /^\/[a-z0-9/_-]*$/i.test(target.pathname) && !/\/(account|orders|checkouts)\//i.test(target.pathname) ? target.pathname : '/other'
        : `external_${slug(target.hostname)}`;
    } catch (_) {
      return '';
    }
  }

  function placement(element) {
    if (element.closest('[data-home-carousel]')) return 'home_carousel';
    if (element.closest('.bbc-wrap-rush-banner')) return 'home_wrap_rush_banner';
    if (element.closest('.bbc-dash-promo')) return 'home_diaper_dash_banner';
    if (element.closest('.home-platform-hero')) return 'home_hero';
    if (element.closest('.home-quick-navigation')) return 'home_quick_navigation';
    if (element.closest('.home-card-row')) return 'home_card_row';
    if (element.closest('.bbc-games-hub')) return 'games_hub';
    if (element.closest('.baby-soother')) return 'baby_soother';
    if (element.closest('.bbc-diaper-dash')) return 'diaper_dash';
    if (element.closest('.bbc-wrap-rush')) return 'diaper_wrap_rush';
    if (element.closest('header')) return 'header';
    if (element.closest('footer')) return 'footer';
    return 'page_content';
  }

  function publish(name, data) {
    // Never fall back to a second GA4 tag in the storefront. Shopify's pixel
    // manager handles consent and the custom pixel forwards these events.
    const analytics = window.Shopify?.analytics;
    if (!analytics || typeof analytics.publish !== 'function') return false;
    try {
      // Keep the Shopify analytics object as `this`; its publisher can rely on
      // its owning API context and silently reject detached method calls.
      const result = analytics.publish(name, data);
      // Shopify's publisher is asynchronous; consume rejected promises so an
      // analytics outage never creates an unhandled storefront rejection.
      if (result && typeof result.catch === 'function') result.catch(() => {});
      return result !== false;
    } catch (_) {
      return false;
    }
  }

  function trackGame(name, input = {}) {
    if (!Object.hasOwn(GAME_EVENT_FIELDS, name)) return false;
    const data = { game_id: 'diaper_dash', page_path: path() };
    for (const field of GAME_EVENT_FIELDS[name]) {
      const value = input[field];
      if (typeof value === 'number' && Number.isFinite(value)) data[field] = Math.max(0, Math.min(10000000, value));
      else if (typeof value === 'boolean') data[field] = value;
      else if (typeof value === 'string' && value.length <= 40 && /^[a-z0-9_-]+$/i.test(value)) data[field] = value;
    }
    if (data.item_type && !['pee', 'poop'].includes(data.item_type)) delete data.item_type;
    if (data.run_type && !['first', 'replay'].includes(data.run_type)) delete data.run_type;
    if (data.entry_point && !['diaper_dash_page'].includes(data.entry_point)) delete data.entry_point;
    if (data.game_state && !['opening', 'playing', 'paused', 'game_over'].includes(data.game_state)) delete data.game_state;
    if (data.pause_source && !['button', 'menu', 'visibility'].includes(data.pause_source)) delete data.pause_source;
    if (data.anger_band && !['none', 'low', 'medium', 'high', 'critical'].includes(data.anger_band)) delete data.anger_band;
    if (data.partner_id && !['1', '2', '3', '4', '5'].includes(String(data.partner_id))) delete data.partner_id;
    if (data.setting && !['sound', 'voice_reactions'].includes(data.setting)) delete data.setting;
    if (data.exit_state && !['playing', 'paused'].includes(data.exit_state)) delete data.exit_state;
    return publish(`bbc:diaper_dash_${name}`, data);
  }

  function bannerData(banner) {
    const slide = banner.matches('[data-carousel-slide]');
    const position = slide ? Array.prototype.indexOf.call(banner.parentElement.children, banner) + 1 : 1;
    const heading = banner.querySelector('.home-platform-carousel__heading, .bbc-wrap-rush-banner__title, .bbc-dash-promo__title, h1, h2');
    const section = placement(banner);
    const title = slug(heading?.textContent || banner.getAttribute('aria-label') || section);
    return {
      promotion_id: `${section}_${slide ? position + '_' : ''}${title}`.slice(0, 100),
      promotion_name: title,
      creative_slot: slide ? `slide_${position}` : section,
      page_path: path(),
    };
  }

  function visible(banner) {
    if (document.visibilityState === 'hidden' || banner.getAttribute('aria-hidden') === 'true' || banner.hasAttribute('inert')) return false;
    const box = banner.getBoundingClientRect();
    const visibleWidth = Math.max(0, Math.min(box.right, window.innerWidth) - Math.max(box.left, 0));
    const visibleHeight = Math.max(0, Math.min(box.bottom, window.innerHeight) - Math.max(box.top, 0));
    return box.width > 0 && box.height > 0 && visibleWidth * visibleHeight >= box.width * box.height * 0.5;
  }

  function scheduleBannerView(banner) {
    if (viewed.has(banner) || viewTimers.has(banner) || !visible(banner)) return;
    const timer = window.setTimeout(() => {
      viewTimers.delete(banner);
      if (!viewed.has(banner) && visible(banner) && publish('bbc:view_promotion', bannerData(banner))) viewed.add(banner);
    }, 1000);
    viewTimers.set(banner, timer);
  }

  function checkBanners() {
    document.querySelectorAll(BANNER_SELECTOR).forEach(scheduleBannerView);
  }

  function initBanners() {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(checkBanners, { threshold: [0, 0.5, 1] });
      document.querySelectorAll(BANNER_SELECTOR).forEach((banner) => observer.observe(banner));
    }
    if ('MutationObserver' in window) {
      document.querySelectorAll('[data-home-carousel]').forEach((root) => {
        new MutationObserver(checkBanners).observe(root, { subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'class'] });
      });
    }
    window.addEventListener('scroll', checkBanners, { passive: true });
    window.addEventListener('resize', checkBanners, { passive: true });
    document.addEventListener('visibilitychange', checkBanners);
    checkBanners();
  }

  function trackClick(event) {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('a, button, summary');
    if (!target || target.matches(':disabled') || target.getAttribute('aria-disabled') === 'true') return;

    const banner = target.closest(BANNER_SELECTOR);
    const bannerLink = target.closest('.home-platform-carousel__button[href], .home-platform-hero__button[href], .bbc-wrap-rush-banner__link, .bbc-dash-promo__card');
    if (banner && bannerLink) {
      publish('bbc:select_promotion', bannerData(banner));
      return;
    }

    const carouselControl = target.closest('[data-carousel-prev], [data-carousel-next], [data-carousel-dot]');
    if (carouselControl) {
      publish('bbc:carousel_navigation', {
        action: carouselControl.hasAttribute('data-carousel-prev') ? 'previous' : carouselControl.hasAttribute('data-carousel-next') ? 'next' : 'dot',
        page_path: path(),
      });
      return;
    }

    const cta = target.closest(CTA_SELECTOR);
    if (!cta) return;
    // Shopify's Google & YouTube pixel already covers ecommerce actions.
    if (cta.closest('form[action*="/cart/add"], [name="add"], [name="checkout"]')) return;
    const action = cta.dataset.bbcCta || cta.dataset.action || cta.dataset.openCategory || cta.dataset.play || cta.dataset.download;
    const dest = destination(cta);
    const id = slug(action || dest || cta.getAttribute('aria-label') || cta.className);
    publish('bbc:cta_click', { cta_id: id, placement: placement(cta), destination_path: dest, page_path: path() });
  }

  function boot() {
    window.BBC_GA4_EVENTS = Object.freeze({ trackGame });
    document.addEventListener('click', trackClick, true);
    initBanners();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
